import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import SEOHead from '../components/SEOHead';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    { icon: 'pi pi-file-edit', title: 'Automated Study Design', description: 'AI-powered extraction of measurable indicators from Terms of Reference.', color: '#3B82F6' },
    { icon: 'pi pi-list-check', title: 'Smart Survey Development', description: 'Transform indicators into validated questionnaire items with skip logic.', color: '#22C55E' },
    { icon: 'pi pi-cloud-upload', title: 'KoboToolbox Integration', description: 'Seamless form deployment with real-time monitoring.', color: '#F97316' },
    { icon: 'pi pi-chart-line', title: 'Statistical Analysis', description: 'Automated data cleaning and publication-ready visualizations.', color: '#A855F7' },
    { icon: 'pi pi-file-pdf', title: 'Report Generation', description: 'Comprehensive research reports with embedded charts.', color: '#EF4444' },
    { icon: 'pi pi-shield', title: 'Audit & Compliance', description: 'Complete audit trails with version control.', color: '#06B6D4' }
  ];

  const stats = [
    { value: '90%', label: 'Time Reduction', icon: 'pi pi-clock', color: '#3B82F6' },
    { value: '10x', label: 'Faster Deployment', icon: 'pi pi-bolt', color: '#F97316' },
    { value: '100%', label: 'Audit Compliance', icon: 'pi pi-verified', color: '#22C55E' },
    { value: '24/7', label: 'Monitoring', icon: 'pi pi-eye', color: '#A855F7' }
  ];

  const workflowSteps = [
    { stage: 1, title: 'TOR Analysis', icon: 'pi pi-file-edit', description: 'Extract indicators', color: '#3B82F6' },
    { stage: 2, title: 'Survey Design', icon: 'pi pi-list-check', description: 'Generate questionnaire', color: '#22C55E' },
    { stage: 3, title: 'Deployment', icon: 'pi pi-cloud-upload', description: 'Deploy to KoboToolbox', color: '#F97316' },
    { stage: 4, title: 'Data Collection', icon: 'pi pi-database', description: 'Real-time monitoring', color: '#06B6D4' },
    { stage: 5, title: 'Analysis', icon: 'pi pi-chart-line', description: 'Statistical analysis', color: '#A855F7' },
    { stage: 6, title: 'Reporting', icon: 'pi pi-file-pdf', description: 'Generate reports', color: '#EF4444' }
  ];

  const gridStyle = (cols: number) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: '1rem'
  });

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  };

  const iconStyle = (color: string): React.CSSProperties => ({
    width: '3.5rem',
    height: '3.5rem',
    borderRadius: '50%',
    backgroundColor: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  });

  return (
    <div>
      <SEOHead 
        title="Automate Your Research Lifecycle with AI Agents"
        description="Transform manual research processes into fully automated, auditable pipelines."
        keywords="research automation, AI agents, survey automation, KoboToolbox"
      />
      
      {/* Hero Section */}
      <div style={{ padding: '3rem 2rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <span style={{ color: '#3B82F6', fontWeight: 'bold', fontSize: '0.875rem' }}>
          <i className="pi pi-sparkles" style={{ marginRight: '0.5rem' }}></i>AI-POWERED RESEARCH AUTOMATION
        </span>
        <h1 style={{ fontSize: '2.5rem', margin: '1rem 0', color: '#1f2937' }}>Automate Your Entire</h1>
        <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem 0', color: '#3B82F6' }}>Research Lifecycle</h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem', maxWidth: '700px', margin: '0 auto 2rem auto', lineHeight: '1.6' }}>
          Transform manual research processes into fully automated, auditable pipelines. 
          From Terms of Reference to policy recommendations using coordinated AI agents.
        </p>
        <div>
          <Button label="Explore Platform" icon="pi pi-arrow-right" className="p-button-lg" style={{ marginRight: '1rem' }} onClick={() => navigate('/platform')} />
          <Button label="View Solutions" icon="pi pi-lightbulb" className="p-button-outlined p-button-lg" onClick={() => navigate('/solutions')} />
        </div>
      </div>

      {/* Stats Section */}
      <div style={{ padding: '2rem', backgroundColor: '#e5e7eb' }}>
        <div style={gridStyle(4)}>
          {stats.map((stat, index) => (
            <div key={index} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>{stat.value}</div>
                  <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>{stat.label}</div>
                </div>
                <div style={iconStyle(stat.color)}>
                  <i className={stat.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span style={{ color: '#3B82F6', fontWeight: 'bold', fontSize: '0.875rem' }}>
            <i className="pi pi-star-fill" style={{ marginRight: '0.5rem' }}></i>PLATFORM CAPABILITIES
          </span>
          <h2 style={{ fontSize: '2rem', margin: '0.5rem 0', color: '#1f2937' }}>Complete Research Automation Platform</h2>
          <p style={{ color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
            Our AI-orchestrated platform handles every step of your research workflow.
          </p>
        </div>

        <div style={gridStyle(3)}>
          {features.map((feature, index) => (
            <div key={index} style={{ ...cardStyle, textAlign: 'center' }}>
              <div style={{ ...iconStyle(feature.color), margin: '0 auto 1rem auto' }}>
                <i className={feature.icon} style={{ color: 'white', fontSize: '1.5rem' }}></i>
              </div>
              <h3 style={{ fontSize: '1.125rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{feature.title}</h3>
              <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem', lineHeight: '1.5' }}>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Workflow Section */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#e5e7eb' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span style={{ color: '#3B82F6', fontWeight: 'bold', fontSize: '0.875rem' }}>
            <i className="pi pi-sitemap" style={{ marginRight: '0.5rem' }}></i>AI AGENT WORKFLOW
          </span>
          <h2 style={{ fontSize: '2rem', margin: '0.5rem 0', color: '#1f2937' }}>6-Stage Automated Research Process</h2>
        </div>

        <div style={gridStyle(3)}>
          {workflowSteps.map((item, index) => (
            <div key={index} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ ...iconStyle(item.color), width: '2.5rem', height: '2.5rem', flexShrink: 0 }}>
                  <i className={item.icon} style={{ color: 'white', fontSize: '1rem' }}></i>
                </div>
                <div style={{ marginLeft: '1rem' }}>
                  <span style={{ color: '#3B82F6', fontSize: '0.75rem', fontWeight: 'bold' }}>STAGE {item.stage}</span>
                  <h3 style={{ fontSize: '1rem', margin: '0.25rem 0', color: '#1f2937' }}>{item.title}</h3>
                  <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Button label="Explore Full Workflow" icon="pi pi-arrow-right" className="p-button-outlined" onClick={() => navigate('/platform/workflow')} />
        </div>
      </div>

      {/* Time Comparison Section */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#f8f9fa' }}>
        <div style={gridStyle(2)}>
          <div style={{ ...cardStyle, borderLeft: '4px solid #EF4444' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <i className="pi pi-clock" style={{ color: '#EF4444', fontSize: '1.5rem', marginRight: '0.75rem' }}></i>
              <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937' }}>Manual Process</span>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#EF4444', marginBottom: '0.5rem' }}>17 Days</div>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Traditional research workflow with manual processes</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <i className="pi pi-times-circle" style={{ color: '#EF4444', marginRight: '0.5rem' }}></i>
                <span style={{ color: '#6b7280' }}>Manual indicator extraction</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <i className="pi pi-times-circle" style={{ color: '#EF4444', marginRight: '0.5rem' }}></i>
                <span style={{ color: '#6b7280' }}>Time-consuming survey design</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-times-circle" style={{ color: '#EF4444', marginRight: '0.5rem' }}></i>
                <span style={{ color: '#6b7280' }}>Error-prone data processing</span>
              </li>
            </ul>
          </div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #22C55E' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <i className="pi pi-bolt" style={{ color: '#22C55E', fontSize: '1.5rem', marginRight: '0.75rem' }}></i>
              <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937' }}>AI-Automated</span>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#22C55E', marginBottom: '0.5rem' }}>20 Minutes</div>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Fully automated pipeline with AI agents</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <i className="pi pi-check-circle" style={{ color: '#22C55E', marginRight: '0.5rem' }}></i>
                <span style={{ color: '#6b7280' }}>Instant indicator extraction</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <i className="pi pi-check-circle" style={{ color: '#22C55E', marginRight: '0.5rem' }}></i>
                <span style={{ color: '#6b7280' }}>Automated survey generation</span>
              </li>
              <li style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-check-circle" style={{ color: '#22C55E', marginRight: '0.5rem' }}></i>
                <span style={{ color: '#6b7280' }}>Real-time analysis & reporting</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#3B82F6', textAlign: 'center' }}>
        <h2 style={{ color: 'white', fontSize: '2rem', margin: '0 0 1rem 0' }}>Ready to Transform Your Research Process?</h2>
        <p style={{ color: '#bfdbfe', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
          Join leading research organizations who have automated their workflows and reduced manual effort by 90%.
        </p>
        <Button label="Explore Platform" icon="pi pi-arrow-right" className="p-button-lg" style={{ backgroundColor: 'white', color: '#3B82F6', border: 'none', marginRight: '1rem' }} onClick={() => navigate('/platform')} />
        <Button label="Contact Us" icon="pi pi-envelope" className="p-button-outlined p-button-lg" style={{ borderColor: 'white', color: 'white' }} onClick={() => navigate('/contact')} />
      </div>
    </div>
  );
};

export default HomePage;
